import { isElement } from '@crown/framework/dom';
import {
  type Child,
  type ElNode,
  type Instruction,
  isString,
  type MaybeDynamic,
  raw,
  reader,
  type Reader,
  safeHtml,
} from '@crown/framework/ir';
import type { ButtonProps } from '../Button';
import { Button } from '../Button';
import type { BrandColor } from '../types';
import template from './alert.tsrx';

export interface AlertProps {
  variant?: BrandColor;
  title?: MaybeDynamic<string | undefined>;
  /** Text, an element, or instructions — sanitised markup, say. */
  message: MaybeDynamic<string | HTMLElement | Instruction>;
  icon?: string | HTMLElement;
  actions?: ButtonProps[];
  buttonAlignment?: 'right' | 'under';
  closable?: boolean;
  onClose?: () => void;
  extraClasses?: string | string[];
}

/** What the template renders, with the props already resolved to children. */
export interface AlertTemplateProps {
  readonly classes: string;
  readonly title: Reader<string | undefined>;
  readonly message: Reader<Child>;
  readonly icon: Child | undefined;
  readonly actions: readonly Instruction[];
  readonly actionsUnder: boolean;
  readonly closable: boolean;
  readonly onClose: (event: Event) => void;
}

function alertClasses({ variant = 'info', extraClasses }: AlertProps): string {
  const classes = ['alert', variant];
  if (extraClasses) classes.push(...(Array.isArray(extraClasses) ? extraClasses : [extraClasses]));
  return classes.join(' ');
}

/** An icon is the caller's own markup, as it was when this wrote `innerHTML`; an element is adopted. */
function iconChild(icon: AlertProps['icon']): Child | undefined {
  if (icon === undefined) return undefined;
  return isString(icon) ? raw(safeHtml(icon)) : icon.cloneNode(true);
}

function closeHandler(onClose: AlertProps['onClose']): (event: Event) => void {
  return (event) => {
    event.stopPropagation();
    onClose?.();
    const target = event.currentTarget;
    if (isElement(target)) target.closest('[role="alert"]')?.remove();
  };
}

const AlertTemplate: (props: AlertTemplateProps) => Instruction = template;

/** The alert as an instruction. */
export function Alert(props: AlertProps): ElNode {
  const node = AlertTemplate({
    classes: alertClasses(props),
    title: reader(props.title),
    message: reader(props.message),
    icon: iconChild(props.icon),
    actions: (props.actions ?? []).map((action) => Button({ variant: 'ghost', size: 'small', ...action })),
    actionsUnder: props.buttonAlignment === 'under',
    closable: props.closable === true,
    onClose: closeHandler(props.onClose),
  });
  if (node.t !== 'el') throw new Error('Alert renders one element');
  return node;
}
